// hooks/use-wishlist-actions.js
import React, {useState, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useCustomerId, useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {useWishList} from '@salesforce/retail-react-app/app/hooks/use-wish-list'
import {useCustomToast} from '../contexts/custom-toast-context'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'

import {Button} from '@salesforce/retail-react-app/app/components/shared/ui'

import {sendWishlistEvent} from '../utils/gtm-utils'

export const useWishlistActions = () => {
    const {formatMessage} = useIntl()
    const navigate = useNavigation()
    const customerId = useCustomerId()
    const {showCustomToast} = useCustomToast()
    const {data: wishlist} = useWishList()
    const [wishlistLoading, setWishlistLoading] = useState([])

    // Mutations
    const {mutateAsync: createCustomerProductListItem} = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const {mutateAsync: deleteCustomerProductListItem} = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )
    const {mutateAsync: updateCustomerProductList} = useShopperCustomersMutation(
        'updateCustomerProductList'
    )

    // Toast styles - empty object to avoid overriding default fixed positioning
    const wishlistToastStyles = {}

    // Check if product is in wishlist
    const isInWishlist = useMemo(() => {
        return (productId) => {
            if (!wishlist?.customerProductListItems || !productId) return false
            return !!wishlist.customerProductListItems.find((item) => item.productId === productId)
        }
    }, [wishlist?.customerProductListItems])

    // Check if product is loading
    const isProductLoading = useMemo(() => {
        return (productId) => wishlistLoading.includes(productId)
    }, [wishlistLoading])

    // Add item to wishlist
    const addItemToWishlist = async (product, containerRef = null) => {
        if (!wishlist?.id || !customerId) {
            console.warn('Wishlist or customer not available')
            return
        }

        setWishlistLoading((prev) => [...prev, product.productId])

        try {
            await createCustomerProductListItem({
                parameters: {customerId, listId: wishlist.id},
                body: {
                    quantity: 1,
                    public: false,
                    priority: 1,
                    type: 'product',
                    productId: product.productId
                }
            })

            // Success toast
            showCustomToast({
                message: formatMessage({
                    id: 'cart.info.item_added',
                    defaultMessage: 'Added to Wishlist'
                }),
                action: (
                    <Button
                        variant="link"
                        fontWeight="medium"
                        fontSize="sm"
                        onClick={() => navigate('/account/wishlist')}
                        colorScheme="white"
                        textDecoration="underline"
                        _hover={{
                            color: 'white'
                        }}
                    >
                        {formatMessage({
                            id: 'cart.info.view_list',
                            defaultMessage: 'View List'
                        })}
                    </Button>
                ),
                styles: wishlistToastStyles
            })
            // GTM Modification
            sendWishlistEvent(product, false)
        } catch (error) {
            console.error('Error adding to wishlist:', error)
            showCustomToast({
                message: formatMessage(API_ERROR_MESSAGE),
                status: 'error',
                styles: wishlistToastStyles
            })
        } finally {
            setWishlistLoading((prev) => prev.filter((id) => id !== product.productId))
        }
    }

    // Remove item from wishlist
    const removeItemFromWishlist = async (product, containerRef = null, context) => {
        if (!wishlist?.id || !customerId) {
            console.warn('Wishlist or customer not available')
            return
        }

        const wishlistItem = wishlist.customerProductListItems?.find(
            (item) => item.productId === product.productId
        )

        if (!wishlistItem) {
            console.warn('Product not found in wishlist')
            return
        }

        setWishlistLoading((prev) => [...prev, product.productId])

        try {
            await deleteCustomerProductListItem({
                parameters: {
                    customerId,
                    listId: wishlist.id,
                    itemId: wishlistItem.id
                }
            })

            let toastMessage = formatMessage({
                id: 'cart.info.item_removed',
                defaultMessage: 'Item removed'
            })

            if (context === 'add-to-cart-context') {
                toastMessage = formatMessage({
                    id: 'cart.info.item_added_to_bag',
                    defaultMessage: 'Item added to shopping bag'
                })
            }

            // Success toast
            showCustomToast({
                message: toastMessage,
                styles: wishlistToastStyles
            })
            // GTM Modification
            sendWishlistEvent(product, true)
        } catch (error) {
            console.error('Error removing from wishlist:', error)
            showCustomToast({
                message: formatMessage(API_ERROR_MESSAGE),
                status: 'error',
                styles: wishlistToastStyles
            })
        } finally {
            setWishlistLoading((prev) => prev.filter((id) => id !== product.productId))
        }
    }

    // Toggle wishlist status
    const toggleWishlist = async (product, containerRef = null) => {
        const isCurrentlyInWishlist = isInWishlist(product.productId)

        if (isCurrentlyInWishlist) {
            await removeItemFromWishlist(product, containerRef)
        } else {
            await addItemToWishlist(product, containerRef)
        }
    }

    // Toggle wishlist public/private status
    const toggleWishlistPublicStatus = async (isPublic) => {
        if (!wishlist?.id || !customerId) {
            console.warn('Wishlist or customer not available')
            return
        }

        try {
            await updateCustomerProductList({
                parameters: {customerId, listId: wishlist.id},
                body: {
                    public: isPublic
                }
            })

            // Clear clipboard when making wishlist private
            if (!isPublic) {
                try {
                    await navigator.clipboard.writeText('')
                } catch (clipboardError) {
                    console.warn('Could not clear clipboard:', clipboardError)
                }
            }
        } catch (error) {
            console.error('Error updating wishlist privacy:', error)
        }
    }

    return {
        isInWishlist,
        isProductLoading,
        addItemToWishlist,
        removeItemFromWishlist,
        toggleWishlist,
        toggleWishlistPublicStatus,
        wishlistLoading
    }
}
